import { createSolidStreamingMarkdown, SolidStreamingMarkdown } from "@storytell/markdown";
import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbChevronDown, TbDownload, TbFileText } from "solid-icons/tb";
import { createEffect, createMemo, createResource, createSignal, onCleanup, Show } from "solid-js";
import { defaultMarkdownOptions } from "../block/markdownOptions";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { type MediaStorageLocation, useChatRuntime } from "../runtime/ChatRuntime";
import { ProcessingSpinner, ScrollableFadeContainer } from "../ui";
import { formatDurationMs } from "../util/dateFormat";
import { sanitizeForFilename } from "../util/download";

interface ReportPreviewCardProps {
  block: StreamingBlock;
}

interface ReportPayload {
  type: "report";
  markdownStorageLocation?: MediaStorageLocation;
  metadata?: {
    title?: string;
    description?: string;
    contentLength?: number;
  };
}

const formatContentLength = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Report card for hosts without report actions (chief) — the body is fetched
 * through the host's backend (`fetchGeneratedText`) and rendered in place, with
 * a native download via `saveGeneratedFile`. bluesky keeps its rich app-side
 * ReportRenderer (PDF/KB-save/Docs export) registered instead.
 */
export function ReportPreviewCard(props: ReportPreviewCardProps) {
  const rt = useChatRuntime();

  const payload = createMemo<ReportPayload | null>(() => {
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      const parsed = safeParseBlockJson(props.block.finalContent.text) as ReportPayload | null;
      if (parsed?.type === "report") return parsed;
    }
    const content = getBlockContent(props.block.blockId);
    if (content) {
      const parsed = safeParseBlockJson(content) as ReportPayload | null;
      if (parsed?.type === "report") return parsed;
    }
    return null;
  });

  const isStreaming = () => !props.block.isComplete;
  const metadata = () => payload()?.metadata;
  const location = () => payload()?.markdownStorageLocation;
  const canRead = () => Boolean(rt.actions.fetchGeneratedText) && Boolean(location());

  const [expanded, setExpanded] = createSignal(false);

  // Fetched on first expand; the streamer renders the whole document at once.
  const [body] = createResource(
    () => {
      const fetchText = rt.actions.fetchGeneratedText;
      const loc = location();
      return expanded() && fetchText && loc ? { fetchText, loc } : undefined;
    },
    ({ fetchText, loc }) => fetchText(loc)
  );

  const markdown = createMemo(() => {
    const text = body.state === "ready" ? body() : undefined;
    if (!text) return null;
    const streamer = createSolidStreamingMarkdown();
    streamer.append(text);
    return streamer;
  });

  const handleDownload = () => {
    const text = body();
    const title = metadata()?.title || "report";
    if (!text) return;
    void rt.actions.saveGeneratedFile?.({
      filename: `${sanitizeForFilename(title)}.md`,
      mimeType: "text/markdown",
      text,
    });
  };

  const [elapsedMs, setElapsedMs] = createSignal(0);
  createEffect(() => {
    if (isStreaming()) {
      setElapsedMs(0);
      const startTime = Date.now();
      const interval = setInterval(() => setElapsedMs(Date.now() - startTime), 1000);
      onCleanup(() => clearInterval(interval));
    }
  });

  return (
    <div
      data-renderer="report-preview-card"
      class="overflow-hidden rounded-xl border border-primary-border/50 text-base"
    >
      <Show
        when={payload()}
        fallback={
          <Show when={isStreaming()}>
            <div class="flex items-center gap-3 p-5 text-sm text-secondary-text">
              <ProcessingSpinner class="size-4" />
              <span>{rt.t("generating_report")}</span>
              <Show when={elapsedMs() > 0}>
                <span class="text-xs text-muted-text">({formatDurationMs(elapsedMs())})</span>
              </Show>
            </div>
          </Show>
        }
      >
        <div class="p-4">
          <div class="flex items-start gap-3">
            <div class="shrink-0 rounded-xl bg-violet-100 p-2.5 dark:bg-violet-900/30">
              <TbFileText size={24} class="text-violet-600 dark:text-violet-400" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-baseline gap-2">
                <Show when={metadata()?.title}>
                  <h3 class="font-medium text-primary-text">{metadata()?.title}</h3>
                </Show>
                <Show when={metadata()?.contentLength}>
                  {(contentLength) => (
                    <span class="text-xs text-muted-text italic">{formatContentLength(contentLength())}</span>
                  )}
                </Show>
              </div>
              <Show when={metadata()?.description}>
                <p class="mt-1 line-clamp-2 text-sm text-secondary-text">{metadata()?.description}</p>
              </Show>

              <Show when={canRead()}>
                <div class="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    class="flex items-center gap-1.5 rounded-lg border border-muted-border bg-tile px-3 py-2 text-sm transition-colors hover:bg-tile-hover"
                  >
                    <TbChevronDown
                      class="size-4 text-secondary-text transition-transform"
                      classList={{ "-rotate-90": !expanded() }}
                    />
                    {rt.t("common.open")}
                  </button>
                  <Show when={rt.actions.saveGeneratedFile && body.state === "ready" && body()}>
                    <button
                      type="button"
                      onClick={handleDownload}
                      aria-label={rt.t("tooltip_download")}
                      class="flex items-center gap-1.5 rounded-lg border border-muted-border bg-tile px-3 py-2 text-sm transition-colors hover:bg-tile-hover"
                    >
                      <TbDownload class="size-4 text-secondary-text" />
                      {rt.t("tooltip_download")}
                    </button>
                  </Show>
                </div>
              </Show>
            </div>
          </div>
        </div>

        <Show when={expanded()}>
          <Show
            when={markdown()}
            fallback={
              <div class="flex items-center justify-center border-t border-primary-border/50 bg-inset p-8">
                <Show
                  when={!body.error}
                  fallback={<span class="text-sm text-secondary-text">{rt.t("chat_failed")}</span>}
                >
                  <ProcessingSpinner class="size-6" />
                </Show>
              </div>
            }
          >
            {(streamer) => (
              <div class="border-t border-primary-border/50 px-4 py-3">
                <ScrollableFadeContainer maxHeight="480px">
                  <div class="max-w-full">
                    <SolidStreamingMarkdown doc={streamer().doc} options={defaultMarkdownOptions} />
                  </div>
                </ScrollableFadeContainer>
              </div>
            )}
          </Show>
        </Show>
      </Show>
    </div>
  );
}
